import { View, Text } from 'react-native';
import React, { useState } from 'react';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Svg, { Circle, Line } from 'react-native-svg';

const STEPS = ['Submit info', 'Processing', 'Under review', 'Approved', 'Declined'];

const STATUS_STEP = {
    CREATED: 1,
    IN_PROGRESS: 1,
    NEEDS_INFORMATION: 1,
    PROCESSING: 2,
    IN_REVIEW: 3,
    COMPLETED: 4,
    ACTIVE: 4,
    DECLINED: 5,
    CANCELED: 5,
};

const DOT_RADIUS = 11;
const INNER_DOT_RADIUS = 4;
const STROKE_WIDTH = 2;
const CY = DOT_RADIUS + STROKE_WIDTH / 2;
const SVG_HEIGHT = DOT_RADIUS * 2 + STROKE_WIDTH;

export default function GivingStepper({ status }) {
    const { theme: { colors } } = useUnistyles();
    const currentStep = STATUS_STEP[(status || '').toUpperCase()] ?? 1;
    const [width, setWidth] = useState(0);

    const stepWidth = width / STEPS.length;
    const centerX = (index) => stepWidth * (index + 0.5);

    return (
        <View style={styles.container} testID="giving-stepper">
            <View style={styles.svgWrapper} onLayout={(e) => setWidth(e?.nativeEvent?.layout?.width ?? 0)}>
                {width > 0 && (
                    <Svg width={width} height={SVG_HEIGHT} viewBox={`0 0 ${width} ${SVG_HEIGHT}`}>
                        {STEPS.slice(1).map((_, index) => {
                            const rightStep = index + 2;
                            const isActive = rightStep <= currentStep;
                            const x1 = centerX(index);
                            const x2 = centerX(index + 1);
                            return (
                                <Line
                                    key={`line-${index}`}
                                    x1={x1}
                                    y1={CY}
                                    x2={x2}
                                    y2={CY}
                                    stroke={isActive ? colors.giving : colors.border}
                                    strokeWidth={2}
                                />
                            );
                        })}
                        {STEPS.map((label, index) => {
                            const step = index + 1;
                            const isActive = step <= currentStep;
                            const isCurrent = step === currentStep;
                            const cx = centerX(index);

                            if (isCurrent) {
                                return (
                                    <React.Fragment key={label}>
                                        <Circle
                                            cx={cx}
                                            cy={CY}
                                            r={DOT_RADIUS}
                                            fill={colors.givingContainer}
                                            stroke={colors.giving}
                                            strokeWidth={2}
                                        />
                                        <Circle
                                            cx={cx}
                                            cy={CY}
                                            r={INNER_DOT_RADIUS}
                                            fill={colors.giving}
                                        />
                                    </React.Fragment>
                                );
                            }

                            return (
                                <Circle
                                    key={label}
                                    cx={cx}
                                    cy={CY}
                                    r={DOT_RADIUS}
                                    fill={isActive ? colors.giving : colors.border}
                                />
                            );
                        })}
                    </Svg>
                )}
            </View>

            <View style={styles.labelsRow}>
                {STEPS.map((label, index) => {
                    const step = index + 1;
                    const isActive = step <= currentStep;
                    const isCurrent = step === currentStep;
                    const isDeclined = (status || '').toUpperCase() === 'DECLINED' && step === 5;

                    return (
                        <Text
                            key={label}
                            style={[
                                styles.label,
                                isActive && styles.activeLabel,
                                isCurrent && styles.currentLabel,
                                isDeclined && styles.declinedLabel,
                            ]}
                            testID={`giving-step-label-${index}`}
                        >
                            {label}
                        </Text>
                    );
                })}
            </View>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    container: {
        paddingVertical: spacing.md,
    },
    svgWrapper: {
        width: '100%',
        height: SVG_HEIGHT,
    },
    labelsRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: spacing.xs,
    },
    label: {
        flex: 1,
        fontSize: typography.micro,
        fontFamily: fonts.body,
        color: colors.textTertiary,
        textAlign: 'center',
    },
    activeLabel: {
        color: colors.giving,
        fontFamily: fonts.bold,
    },
    currentLabel: {
        color: colors.giving,
    },
    declinedLabel: {
        color: colors.error,
    },
}));
